import React, { useMemo, useEffect, useState, useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import DatePicker from 'antd/lib/date-picker'
import TimePicker from 'antd/lib/time-picker'
import moment from 'moment-timezone'
import classnames from 'classnames'

import { constants, dateFormatter } from 'helpers'
import UikFormInputGroup from 'components/UikFormInputGroup'
import UikSelect from 'components/UikSelect'

import cls from '../EventForm.module.scss'
import CardHeader from '../components/CardHeader'
import FieldTitle from '../components/FieldTitle'
import { capitalizeFirstLetter } from '../../../helpers/strings'
import { miscellaneousToOption } from '../utils'

// Assets

const {
  EVENT_CONSTANTS: { NON_ADMIN_START_WEEKS, RECURRING_FREQUENCY, RECURRING_MAX_QUANTITY },
} = constants

const defaultAdminStartTime = moment().add(1, 'hour').startOf('hour')
const defaultStartTime = moment().add(NON_ADMIN_START_WEEKS, 'week').add(1, 'hour').startOf('hour')
const disallowedMinutes = [...Array(60).keys()].filter(n => ![0, 30].includes(n))
const currentTimezone = dateFormatter.getLongTimeZone()
const emptyValue = [{ value: '', label: 'Once' }]
const recurrentFrequencyValues = Object.values(RECURRING_FREQUENCY).map(item => ({
  value: item,
  label: capitalizeFirstLetter(item),
}))
const recurrentFrequencyOptions = [...emptyValue, ...recurrentFrequencyValues]

const DateAndTimeStep = ({ formState, setFormState, adminMode, locations, errors }) => {
  const locationOptions = useMemo(
    () => locations.map(location => miscellaneousToOption(location)),
    [locations]
  )
  const [locationOption, setLocationOption] = useState(null)

  const handleLocationChange = useCallback(
    selected => {
      setLocationOption([selected])
      setFormState({ location: selected.value })
    },
    [setFormState]
  )

  const disablesDatesBeforeDefaultStartTime = currentDate => {
    const startTime = adminMode ? defaultAdminStartTime : defaultStartTime
    return currentDate && currentDate.isBefore(startTime, 'day')
  }

  const onDateChange = date => {
    setFormState({ startTime: date.startOf('day') })
  }

  const onChangeTime = time => {
    const newTime = formState.startTime.hours(time.hours()).minutes(time.minutes()).seconds(0)
    setFormState({ startTime: newTime })
  }

  const onChangeRecurrence = recurrenceFrequency => {
    setFormState({
      recurrenceQuantity: recurrenceFrequency ? RECURRING_MAX_QUANTITY : null,
      recurrenceFrequency: recurrenceFrequency || null,
    })
  }

  useEffect(() => {
    if (!isEmpty(locationOptions)) {
      handleLocationChange(locationOptions[0])
    }
  }, [locationOptions, handleLocationChange])

  return (
    <div className={cls.cardContainer}>
      <CardHeader stepNumber="4" title={'Event Time & Location'} showInfoCircle={false} />
      <div className={cls.card}>
        <div className={cls.fieldContainer}>
          <FieldTitle title="Date of Event" />
          <DatePicker
            format="YYYY-MM-DD"
            onChange={onDateChange}
            disabledDate={disablesDatesBeforeDefaultStartTime}
            defaultValue={formState.startTime}
            showToday={adminMode}
            value={formState.startTime}
            allowClear={false}
            className={classnames(cls.datePicker, {
              [cls.timeError]: errors.startTime,
            })}
            name="eventDate"
          />
        </div>
        <div className={cls.dualFieldContainer}>
          <div className={cls.fieldContainer}>
            <FieldTitle title="Event Time" />
            <TimePicker
              use12Hours={true}
              format="h:mm A"
              onChange={onChangeTime}
              disabledMinutes={() => (adminMode ? [] : disallowedMinutes)}
              hideDisabledOptions={true}
              defaultValue={formState.startTime}
              value={formState.startTime}
              allowClear={false}
              className={classnames(cls.timePicker, {
                [cls.timeError]: errors.startTime,
              })}
              popupClassName={cls.pickerPopup}
              name="eventTime"
            />
          </div>
          <div className={cls.fieldContainer}>
            <FieldTitle title="Time Zone" />
            <div className={cls.timezone}>{currentTimezone}</div>
          </div>
        </div>

        <div className={cls.fieldContainer}>
          <FieldTitle title="Event Location" />
          <div className={cls.selectWrappper}>
            <UikFormInputGroup horizontal>
              <UikSelect
                className={cls.selectItem}
                position="bottomRight"
                name="location"
                options={locationOptions}
                value={locationOption}
                defaultValue={locationOption}
                onChange={handleLocationChange}
              />
            </UikFormInputGroup>
          </div>
        </div>
        {adminMode && (
          <div className={cls.fieldContainer}>
            <FieldTitle
              title="Frequency"
              showInfoCircle={true}
              info="Teleport will display the next 3 events to the daters at any time when they are purchasing a ticket to your event."
            />
            <div className={cls.selectWrappper}>
              <UikFormInputGroup horizontal>
                <UikSelect
                  placeholder={''}
                  className={cls.selectItem}
                  options={recurrentFrequencyOptions}
                  position="bottomRight"
                  name="recurrenceFrequency"
                  defaultValue={emptyValue}
                  onChange={selected => onChangeRecurrence(selected.value)}
                />
              </UikFormInputGroup>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DateAndTimeStep
